import { Label } from "../../ui/label";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

export const CHARGE_PAYMENT_METHOD_OPTIONS = [
  { value: "bank_transfer", label: "Bank Transfer" },
  { value: "eftpos", label: "EFTPOS" },
  { value: "cash", label: "Cash" },
  { value: "other", label: "Other" },
];

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const MODES = [
  { value: "stripe_saved_card", title: "Charge saved card now", desc: "Charges the customer's card on file straight away and emails a receipt. If the card is declined, nothing is recorded." },
  { value: "stripe", title: "Send Stripe payment link", desc: "Emails a secure Stripe link. Marked paid automatically when the customer pays." },
  { value: "external_paid", title: "Paid externally", desc: "Money already received by bank transfer, EFTPOS or cash. Records it as paid and emails a receipt." },
  { value: "external_pending", title: "Collect externally later", desc: "Shows as outstanding on the booking until you mark it paid." },
  { value: "bond_deduction", title: "Deduct from security bond", desc: "Takes all or part of the charge from the bond and lowers the refundable bond." },
];
const REMAINDER_MODES = MODES.filter((m) => m.value !== "bond_deduction");

function ModeRadio({ opt, checked, disabled, note, onSelect, name, testPrefix }) {
  return (
    <label
      className={`flex items-start gap-2 p-3 border transition-colors ${disabled ? "opacity-50 cursor-not-allowed border-[#282828]/10" : "cursor-pointer"} ${checked ? "border-[#9A7B4F] bg-[#9A7B4F]/5" : "border-[#282828]/10 hover:bg-[#FAF8F0]"}`}
      data-testid={`${testPrefix}-${opt.value}`}
    >
      <input type="radio" name={name} checked={checked} disabled={disabled} onChange={onSelect} className="mt-0.5" />
      <div>
        <div className="text-[12px] font-semibold text-[#282828]">{opt.title}</div>
        <div className="text-[11px] text-[#676664]/70 mt-0.5">{opt.desc}</div>
        {note && <div className="text-[11px] text-[#B8761C] mt-1">{note}</div>}
      </div>
    </label>
  );
}

function ExternalPaidFields({ method, date, onMethod, onDate, testPrefix }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      <div>
        <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Payment Method</Label>
        <Select value={method} onValueChange={onMethod}>
          <SelectTrigger className="mt-1 border-[#282828]/10" data-testid={`${testPrefix}-payment-method-select`}><SelectValue /></SelectTrigger>
          <SelectContent>{CHARGE_PAYMENT_METHOD_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div>
        <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Payment Date</Label>
        <Input type="date" value={date} onChange={(e) => onDate(e.target.value)} className="mt-1 border-[#282828]/10" data-testid={`${testPrefix}-payment-date-input`} />
      </div>
    </div>
  );
}

function BondSplitSection({ draft, setDraft, meta }) {
  const available = Number(meta?.bond_summary?.available) || 0;
  const amount = Number(draft.amount) || 0;
  const bondAmt = draft.bond_amount === "" ? 0 : Number(draft.bond_amount) || 0;
  const remainder = Math.max(+(amount - bondAmt).toFixed(2), 0);
  const savedCard = !!meta?.saved_card_available;
  return (
    <div className="border border-[#9A7B4F]/25 bg-[#FAF8F0] p-4 space-y-4" data-testid="bond-split-section">
      <div className="grid grid-cols-3 gap-3 text-[11px]">
        <div><div className="text-[#676664]/60 uppercase tracking-wider text-[9px]">Bond held</div><div className="text-[#282828]" data-testid="bond-deposit-value">{money(meta?.bond_summary?.deposit)}</div></div>
        <div><div className="text-[#676664]/60 uppercase tracking-wider text-[9px]">Already deducted</div><div className="text-[#282828]">{money(meta?.bond_summary?.deducted_by_charges)}</div></div>
        <div><div className="text-[#676664]/60 uppercase tracking-wider text-[9px]">Available</div><div className="text-[#9A7B4F] font-semibold" data-testid="bond-available-value">{money(available)}</div></div>
      </div>
      <div>
        <Label className="text-[10px] tracking-wider uppercase text-[#676664]/60">Amount to take from bond ($)</Label>
        <Input type="number" step="0.01" min="0" max={Math.min(available, amount || available)} value={draft.bond_amount}
          onChange={(e) => setDraft({ ...draft, bond_amount: e.target.value })}
          className="mt-1 border-[#282828]/10 bg-white" data-testid="bond-amount-input" />
        {bondAmt > available + 0.001 && <p className="text-[11px] text-red-600 mt-1">Only {money(available)} of the bond is available.</p>}
        {amount > 0 && bondAmt > 0 && bondAmt <= available && (
          <p className="text-[11px] text-[#676664]/70 mt-1" data-testid="bond-after-text">Refundable bond after this: <strong>{money(available - bondAmt)}</strong></p>
        )}
      </div>
      {remainder > 0 && (
        <div className="space-y-2" data-testid="remainder-section">
          <Label className="text-[10px] tracking-wider uppercase text-[#676664]/70 font-semibold block">
            Collect the remaining <span className="text-[#B23A48]" data-testid="remainder-amount">{money(remainder)}</span> by
          </Label>
          {REMAINDER_MODES.map((opt) => (
            <ModeRadio key={opt.value} opt={opt} name="chargeRemainderMode" testPrefix="remainder-mode"
              checked={draft.remainder_mode === opt.value}
              disabled={opt.value === "stripe_saved_card" && !savedCard}
              note={opt.value === "stripe_saved_card" && !savedCard ? "No saved card on file for this customer." : null}
              onSelect={() => setDraft({ ...draft, remainder_mode: opt.value })} />
          ))}
          {draft.remainder_mode === "external_paid" && (
            <ExternalPaidFields testPrefix="remainder" method={draft.remainder_payment_method} date={draft.remainder_payment_date}
              onMethod={(v) => setDraft({ ...draft, remainder_payment_method: v })}
              onDate={(v) => setDraft({ ...draft, remainder_payment_date: v })} />
          )}
        </div>
      )}
    </div>
  );
}

export function ChargeCollectionOptions({ draft, setDraft, meta }) {
  const bs = meta?.bond_summary;
  const bondDisabled = !bs || !!bs.blocker || (Number(bs.available) || 0) <= 0;
  const bondNote = !bs ? null : bs.blocker || ((Number(bs.available) || 0) <= 0 ? "The whole bond has already been deducted." : `${money(bs.available)} available`);
  const savedCard = !!meta?.saved_card_available;
  const selectMode = (v) => {
    const next = { ...draft, collection_mode: v };
    if (v === "bond_deduction" && draft.bond_amount === "") {
      const amt = Number(draft.amount) || 0;
      next.bond_amount = amt > 0 ? Math.min(amt, Number(bs?.available) || 0).toFixed(2) : "";
    }
    setDraft(next);
  };
  return (
    <div className="space-y-4">
      <Label className="text-[10px] tracking-wider uppercase text-[#676664]/70 font-semibold mb-2 block">How will this be collected?</Label>
      <div className="space-y-2">
        {MODES.map((opt) => {
          const disabled = (opt.value === "bond_deduction" && bondDisabled) || (opt.value === "stripe_saved_card" && !savedCard);
          const note = opt.value === "bond_deduction" ? bondNote : (opt.value === "stripe_saved_card" && !savedCard ? "No saved card on file for this customer." : null);
          return (
            <ModeRadio key={opt.value} opt={opt} name="chargeCollectionMode" testPrefix="collection-mode"
              checked={draft.collection_mode === opt.value} disabled={disabled} note={note}
              onSelect={() => selectMode(opt.value)} />
          );
        })}
      </div>
      {draft.collection_mode === "external_paid" && (
        <ExternalPaidFields testPrefix="charge" method={draft.payment_method} date={draft.payment_date}
          onMethod={(v) => setDraft({ ...draft, payment_method: v })}
          onDate={(v) => setDraft({ ...draft, payment_date: v })} />
      )}
      {draft.collection_mode === "bond_deduction" && <BondSplitSection draft={draft} setDraft={setDraft} meta={meta} />}
    </div>
  );
}
